import { CloseOutlined } from '@ant-design/icons';
import {
  MyLeadsFilterTypes,
  MyLeadsFriendlyName,
  MyLeadsSearchQuery,
  SubsectionKeys,
} from '@mm/state/interfaces/myLeads';
import {
  combinedFeatureFlagsSelector,
  companyFeatureFlagsSelector,
} from '@mm/state/selectors/featureFlagsSelectors';
import { selectMyLeadsSearchOptions } from '@mm/state/selectors/myLeadsSelectors';
import { InputNumberProps, Spin } from 'antd';
import classNames from 'classnames';
import { isEqual, uniqueId } from 'lodash';
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import CheckBox from '../../../../../../common/components/Checkbox';
import { AgeSlider } from '../../GetLeads/LeadsSearch/LeadsSearchFiltersMenu/AgeSlider/AgeSlider';
import {
  convertToCamesCase,
  convertToUpperSnakeCase,
} from '../../GetLeads/LeadsSearch/LeadsSearchFiltersMenu/helpers';
import { YES_VALUE } from '../../GetLeads/LeadsSearch/LeadsSearchFiltersMenu/LeadsSearchFiltersMenu';
import { Section } from '../../GetLeads/LeadsSearch/LeadsSearchFiltersMenu/Section/Section';
import { Subsection } from '../../GetLeads/LeadsSearch/LeadsSearchFiltersMenu/Subsection/Subsection';
import { FilterByLandingPage } from '../FilterByLandingPage/FilterByLandingPage';
import { FilterByUrlVisited } from '../FilterByUrlVisited/FilterByUrlVisited';
import { DatesPicker } from './DatesPicker/DatesPicker';
import { remappedNames } from './helpers';
import styles from './MyLeadsFiltersPanel.module.css';
import { MyLeadsLocationInputFilter } from './MyLeadsLocationInputFilter/MyLeadsLocationInputFilter';
import { OtherAttributesFilter } from './OtherAttributesFilter/OtherAttributesFilter';
import { PlatformsFilter } from './PlatformsFilter/PlatformsFilter';
import { RadioGroup } from './RadioGroup/RadioGroup';
import { SearchContacts } from './SearchContacts/SearchContacts';
import { TagsFilter } from './TagsFilter/TagsFilter';
import { useFilterParams } from './use-filter-params';

type Props = {
  filterParams: MyLeadsSearchQuery;
  setFilterParams: (params: MyLeadsSearchQuery) => void;
  busy?: boolean;
};

export const MyLeadsFiltersPanel = ({
  filterParams,
  setFilterParams,
  busy,
}: Props) => {
  const searchOptions = useSelector(selectMyLeadsSearchOptions);
  const searchVariablesFiltered = Object.entries(
    searchOptions.variables || {},
  ).filter(item => item[1].length);
  const searchVariablesList = Object.fromEntries(searchVariablesFiltered);
  const searchAttributes = searchOptions.attributes;
  const minAge = 18;
  const maxAge = 100;
  const defaultAgeValue = filterParams?.query?.attributes?.age?.[0]
    ?.split('-')
    ?.map(i => Number(i)) as [number, number];
  const businessContactKey = convertToUpperSnakeCase(SubsectionKeys.leadType);
  const isExcludingBusinessContacts =
    !!filterParams.query?.excludedVariables?.[businessContactKey];
  const isIncludingBusinessContacts =
    !!filterParams.query?.variables?.[businessContactKey];

  const hasVoyagerEnrichmentFeature = useSelector(
    combinedFeatureFlagsSelector,
  ).voyagerEnrichment;
  const hasVoyagerCompanyFlag = useSelector(
    companyFeatureFlagsSelector,
  ).voyager;

  const {
    isFilterParamExist,
    getUpdatedFilterParams,
    hasActiveFilters,
    shouldDisableFilters,
    hasFilters,
  } = useFilterParams({ filterParams });

  const onCheckboxClick = useCallback(
    (name: string, value: string | boolean, type, isExisting) => {
      const updatedQuery = getUpdatedFilterParams({
        key: name,
        value,
        isExisting,
        type,
      });

      setFilterParams(updatedQuery);
    },
    [setFilterParams, getUpdatedFilterParams],
  );

  const renderCheckbox = useCallback(
    (
      name: string,
      label: string,
      {
        isList = false,
        type = MyLeadsFilterTypes.variables,
        showOnlyWhenExists = true,
        isBoolean = false,
        variableName = null,
      }: {
        isBoolean?: boolean;
        type?: MyLeadsFilterTypes;
        variableName?: string | null;
        isList?: boolean;
        showOnlyWhenExists?: boolean;
      } = {},
    ) => {
      if (
        showOnlyWhenExists &&
        (variableName || type !== MyLeadsFilterTypes.attributes)
      ) {
        const fieldname = variableName ? variableName : name;
        if (
          !Object.prototype.hasOwnProperty.call(searchVariablesList, fieldname)
        ) {
          return <></>;
        }
      }
      const remappedLabel = remappedNames(label);

      const value = isList
        ? type === MyLeadsFilterTypes.attributes
          ? remappedLabel.value
          : label
        : isBoolean
        ? true
        : YES_VALUE.toLocaleLowerCase();
      const isChecked = isFilterParamExist({
        key: name,
        value,
        type,
      });

      return (
        <CheckBox
          key={uniqueId()}
          label={remappedLabel.friendlyName}
          name={name}
          isChecked={isChecked}
          disabled={isChecked ? false : shouldDisableFilters}
          className={classNames(styles.checkbox, {
            [styles.listCheckbox]: isList,
          })}
          labelClassName={styles.checkboxLabel}
          onClick={() => onCheckboxClick(name, value, type, isChecked)}
        />
      );
    },
    [
      searchVariablesList,
      onCheckboxClick,
      isFilterParamExist,
      shouldDisableFilters,
    ],
  );

  const renderCheckboxList = useCallback(
    (key: string, isAttribute?: boolean) => {
      if (isAttribute && searchAttributes) {
        return searchAttributes[key]?.map(checkbox =>
          renderCheckbox(key, checkbox, {
            isList: true,
            type: MyLeadsFilterTypes.attributes,
          }),
        );
      }
      return searchVariablesList[key]?.map(checkbox =>
        renderCheckbox(key, checkbox, { isList: true }),
      );
    },
    [renderCheckbox, searchVariablesList, searchAttributes],
  );

  const onSelectAllLeadType = () => {
    const updatedFilterParams = getUpdatedFilterParams({
      key: businessContactKey,
      value: true,
      isExisting: isExcludingBusinessContacts || isIncludingBusinessContacts,
      type: isExcludingBusinessContacts
        ? MyLeadsFilterTypes.excludedVariables
        : MyLeadsFilterTypes.variables,
    });

    setFilterParams(updatedFilterParams);
  };

  const withSpin = (children: JSX.Element | JSX.Element[]) => (
    <Spin size="small" spinning={busy} className={styles.spin}>
      {children}
    </Spin>
  );

  const hideGroupping = (keys: string | string[]) => {
    if (Array.isArray(keys)) {
      return !keys.some(key =>
        Object.prototype.hasOwnProperty.call(searchVariablesList, key),
      );
    }
    return !Object.prototype.hasOwnProperty.call(searchVariablesList, keys);
  };

  const onAfterAgeChange = (ageRange: [number, number]) => {
    const ageStr = isEqual(ageRange, [minAge, maxAge])
      ? ''
      : ageRange.join('-');
    const AGE_KEY = SubsectionKeys.age;

    if (filterParams.query?.attributes?.[AGE_KEY]) {
      delete filterParams.query?.attributes?.[AGE_KEY];
    }

    const updatedQuery = getUpdatedFilterParams({
      key: AGE_KEY,
      value: ageStr,
      isExisting: false,
      type: MyLeadsFilterTypes.attributes,
    });

    setFilterParams(updatedQuery);
  };

  const onResetFilters = () => {
    setFilterParams({ query: {} });
  };

  const renderSubsection = ({
    name,
    key,
    types,
    renderAsList,
    skipHide,
    children,
  }: {
    name: string;
    key: string | string[];
    types: MyLeadsFilterTypes[];
    renderAsList: boolean;
    skipHide?: boolean;
    children?: any;
  }) => (
    <Subsection
      key={Array.isArray(key) ? key.join() : key}
      name={name}
      className={styles.subsection}
      hide={skipHide ? false : hideGroupping(key)}
      hasActiveFilters={hasActiveFilters({
        keys: Array.isArray(key) ? key : [key],
        types,
      })}
    >
      {renderAsList && !Array.isArray(key) ? (
        withSpin(
          renderCheckboxList(
            key,
            types.some(type => type === MyLeadsFilterTypes.attributes),
          ),
        )
      ) : children ? (
        withSpin(children)
      ) : (
        <></>
      )}
    </Subsection>
  );

  const renderSection = ({
    name,
    keys,
    isAttribute,
    children,
    skipHide,
  }: {
    name: string;
    keys: string[] | string[];
    isAttribute: boolean;
    children: any;
    skipHide?: boolean;
  }) => (
    <Section
      name={name}
      hide={skipHide ? false : hideGroupping(keys)}
      hasActiveFilters={hasActiveFilters({
        keys,
        types: [
          isAttribute
            ? MyLeadsFilterTypes.attributes
            : MyLeadsFilterTypes.variables,
        ],
      })}
    >
      {children}
    </Section>
  );

  const renderRadioGroup = useCallback(
    (attrKey: string, inputProps?: InputNumberProps<number>) => (
      <div key={attrKey}>
        <RadioGroup
          attributeKey={attrKey}
          title={MyLeadsFriendlyName[attrKey]}
          filterParams={filterParams}
          setFilterParams={setFilterParams}
          doubleInputs={attrKey === SubsectionKeys.customerJourney[0]}
          useExtendedOptions={attrKey === SubsectionKeys.customerJourney[4]}
          maxInputValue={
            attrKey === SubsectionKeys.customerJourney[0] ? 59 : undefined
          }
          inputProps={inputProps}
        />
      </div>
    ),
    [filterParams, setFilterParams],
  );

  return (
    <div className={styles.container}>
      <div className={styles.title}>
        Filters
        {hasFilters && (
          <div className={styles.resetAll} onClick={onResetFilters}>
            Reset all <CloseOutlined />
          </div>
        )}
      </div>

      <div className={styles.filters}>
        <div className={styles.searchBox}>
          <SearchContacts
            filterParams={filterParams}
            setFilterParams={setFilterParams}
            className={classNames({ [styles.disabled]: busy })}
          />
        </div>
        <Section
          name="Date Added"
          css={{
            title: styles.basicFiltersTitle,
            heading: styles.basicFiltersHeading,
          }}
          arrowStyle="outlined"
          hasActiveFilters={hasActiveFilters({
            keys: ['created_at'],
            types: [MyLeadsFilterTypes.attributes],
          })}
          expandOnMount
        >
          <DatesPicker
            filterParams={filterParams}
            setFilterParams={setFilterParams}
            className={classNames({ [styles.disabled]: busy })}
          />
        </Section>
        {hasVoyagerEnrichmentFeature && hasVoyagerCompanyFlag && (
          <Section
            name="Lead Type"
            css={{
              title: styles.basicFiltersTitle,
              heading: styles.basicFiltersHeading,
            }}
            arrowStyle="outlined"
            hasActiveFilters={hasActiveFilters({
              keys: [SubsectionKeys.leadType],
              types: [
                MyLeadsFilterTypes.variables,
                MyLeadsFilterTypes.excludedVariables,
              ],
            })}
            expandOnMount
          >
            {withSpin(
              <>
                <CheckBox
                  key={uniqueId()}
                  label="All Leads"
                  name="allLeads"
                  isChecked={
                    !isExcludingBusinessContacts && !isIncludingBusinessContacts
                  }
                  disabled={shouldDisableFilters}
                  className={styles.checkbox}
                  labelClassName={styles.checkboxLabel}
                  onClick={onSelectAllLeadType}
                />

                {renderCheckbox(
                  SubsectionKeys.leadType,
                  MyLeadsFriendlyName.CONSUMER_LEADS,
                  {
                    isList: false,
                    type: MyLeadsFilterTypes.excludedVariables,
                    isBoolean: true,
                    variableName: convertToCamesCase(SubsectionKeys.leadType),
                    showOnlyWhenExists: false,
                  },
                )}
                {withSpin(
                  renderCheckbox(
                    SubsectionKeys.leadType,
                    MyLeadsFriendlyName.BUSINESS_CONTACT,
                    {
                      isList: false,
                      type: MyLeadsFilterTypes.variables,
                      isBoolean: true,
                      variableName: convertToCamesCase(SubsectionKeys.leadType),
                      showOnlyWhenExists: false,
                    },
                  ),
                )}
              </>,
            )}
          </Section>
        )}
        <Section
          name="Lead Source"
          css={{
            title: styles.basicFiltersTitle,
            heading: styles.basicFiltersHeading,
          }}
          arrowStyle="outlined"
          hasActiveFilters={hasActiveFilters({
            keys: [SubsectionKeys.source],
            types: [MyLeadsFilterTypes.attributes],
          })}
          expandOnMount
        >
          {withSpin(renderCheckboxList(SubsectionKeys.source, true))}
        </Section>
        <Section
          name="Interactions"
          css={{
            title: styles.basicFiltersTitle,
            heading: styles.basicFiltersHeading,
          }}
          arrowStyle="outlined"
          hasActiveFilters={hasActiveFilters({
            keys: SubsectionKeys.interactions,
            types: [MyLeadsFilterTypes.attributes],
          })}
          expandOnMount
        >
          {withSpin(
            SubsectionKeys.interactions.map(key =>
              renderCheckbox(key, MyLeadsFriendlyName[key], {
                isList: false,
                type: MyLeadsFilterTypes.attributes,
                isBoolean: true,
                variableName: convertToCamesCase(key),
                showOnlyWhenExists: false,
              }),
            ),
          )}
        </Section>
        <Section
          name="Intent"
          css={{
            title: styles.basicFiltersTitle,
            heading: styles.basicFiltersHeading,
          }}
          arrowStyle="outlined"
          hasActiveFilters={hasActiveFilters({
            keys: SubsectionKeys.intent,
            types: [MyLeadsFilterTypes.attributes],
          })}
          expandOnMount
        >
          {withSpin(
            SubsectionKeys.intent.map(key =>
              renderCheckbox(key, MyLeadsFriendlyName[key], {
                isList: false,
                type: MyLeadsFilterTypes.attributes,
                isBoolean: true,
                variableName: convertToCamesCase(key),
                showOnlyWhenExists: false,
              }),
            ),
          )}
        </Section>
        <Section
          name="Customer Journey"
          css={{
            title: styles.basicFiltersTitle,
            heading: styles.basicFiltersHeading,
          }}
          arrowStyle="outlined"
          hasActiveFilters={hasActiveFilters({
            keys: SubsectionKeys.customerJourney,
            types: [
              MyLeadsFilterTypes.attributes,
              MyLeadsFilterTypes.variables,
            ],
          })}
          expandOnMount
        >
          {withSpin(
            <>
              <div className={styles.radioInputsBlock}>
                {SubsectionKeys.customerJourney.map(attrKey =>
                  renderRadioGroup(attrKey),
                )}
              </div>
              <FilterByLandingPage
                attributeKey={SubsectionKeys.landingPageUrl}
                filterParams={filterParams}
                setFilterParams={setFilterParams}
              />
              <FilterByUrlVisited
                filterParams={filterParams}
                setFilterParams={setFilterParams}
              />
            </>,
          )}
        </Section>
        <Section
          name="Conversion Attributes"
          css={{
            title: styles.basicFiltersTitle,
            heading: styles.basicFiltersHeading,
          }}
          arrowStyle="outlined"
          hasActiveFilters={hasActiveFilters({
            keys: SubsectionKeys.revenueAttributes,
            types: [MyLeadsFilterTypes.attributes],
          })}
        >
          {withSpin(
            <>
              {renderCheckbox(
                'klaviyo_purchased',
                MyLeadsFriendlyName.klaviyo_purchased,
                {
                  isList: false,
                  type: MyLeadsFilterTypes.attributes,
                  isBoolean: true,
                },
              )}
              <br />
              {renderRadioGroup('klaviyo_total_revenue', {
                step: 0.1,
                precision: 2,
                addonAfter: '$',
                maxLength: 7,
                style: { width: '140px' },
              })}
            </>,
          )}
        </Section>
        <Section
          name="Other Contact Attributes"
          css={{
            title: styles.basicFiltersTitle,
            heading: styles.basicFiltersHeading,
          }}
          arrowStyle="outlined"
          hasActiveFilters={false}
        >
          {withSpin(
            <OtherAttributesFilter
              filterParams={filterParams}
              setFilterParams={setFilterParams}
              disabled={shouldDisableFilters}
            />,
          )}
        </Section>

        <div style={{ display: 'none' }}>
          <Section
            name="Tags"
            css={{
              title: styles.basicFiltersTitle,
              heading: styles.basicFiltersHeading,
            }}
            arrowStyle="outlined"
            hasActiveFilters={false}
          >
            {withSpin(
              <>
                <TagsFilter
                  title="Include tags"
                  renderCheckbox={renderCheckbox}
                />
                ,
                <TagsFilter
                  title="Exclude tags"
                  renderCheckbox={renderCheckbox}
                  css={{
                    container: styles.excludedTagsContainer,
                    title: styles.excludedTagsTitle,
                  }}
                />
                ,
              </>,
            )}
          </Section>
        </div>
        <Section
          name="Platforms"
          css={{
            title: styles.basicFiltersTitle,
            heading: styles.basicFiltersHeading,
          }}
          arrowStyle="outlined"
          hasActiveFilters={hasActiveFilters({
            keys: [SubsectionKeys.platforms],
            types: [MyLeadsFilterTypes.attributes],
          })}
        >
          {withSpin(
            <PlatformsFilter
              attributeKey={SubsectionKeys.platforms}
              filterParams={filterParams}
              setFilterParams={setFilterParams}
              checkboxDisabled={shouldDisableFilters}
              css={{ checkbox: styles.checkbox, label: styles.checkboxLabel }}
            />,
          )}
        </Section>

        {Boolean(searchVariablesFiltered.length) && (
          <div className={styles.title}>Advanced</div>
        )}
        {renderSection({
          name: 'Professional',
          keys: [
            SubsectionKeys.busOwner,
            SubsectionKeys.credRate,
            SubsectionKeys.income,
            SubsectionKeys.occupation,
            ...SubsectionKeys.profHobby,
          ],
          isAttribute: false,
          children: (
            <>
              {renderSubsection({
                name: MyLeadsFriendlyName.LD_BUS_OWNER,
                key: SubsectionKeys.busOwner,
                types: [MyLeadsFilterTypes.variables],
                renderAsList: true,
              })}
              {renderSubsection({
                name: MyLeadsFriendlyName.LD_CREDRATE,
                key: SubsectionKeys.credRate,
                types: [MyLeadsFilterTypes.variables],
                renderAsList: true,
              })}
              {renderSubsection({
                name: MyLeadsFriendlyName.LD_INCOMESTHH,
                key: SubsectionKeys.income,
                types: [MyLeadsFilterTypes.variables],
                renderAsList: true,
              })}
              {renderSubsection({
                name: MyLeadsFriendlyName.LD_OCCUPATION,
                key: SubsectionKeys.occupation,
                types: [MyLeadsFilterTypes.variables],
                renderAsList: true,
              })}
              {renderSubsection({
                name: 'Professional Hobby',
                key: SubsectionKeys.profHobby,
                types: [MyLeadsFilterTypes.variables],
                renderAsList: false,
                children: (
                  <>
                    {renderCheckbox(
                      'ldInvReal',
                      MyLeadsFriendlyName.LD_INV_REAL,
                    )}
                    {renderCheckbox(
                      'ldInvStocks',
                      MyLeadsFriendlyName.LD_INV_STOCKS,
                    )}
                  </>
                ),
              })}
            </>
          ),
        })}

        {renderSection({
          name: 'Location & Home',
          keys: [
            ...SubsectionKeys.location,
            SubsectionKeys.homeValue,
            SubsectionKeys.homeFuel,
            SubsectionKeys.homeOwn,
          ],
          isAttribute: false,
          children: (
            <>
              {renderSubsection({
                name: MyLeadsFriendlyName.LD_HOMEMKTVAL,
                key: SubsectionKeys.homeValue,
                types: [MyLeadsFilterTypes.variables],
                renderAsList: true,
              })}
              {renderSubsection({
                name: MyLeadsFriendlyName.LD_FUEL,
                key: SubsectionKeys.homeFuel,
                types: [MyLeadsFilterTypes.variables],
                renderAsList: true,
              })}

              {renderSubsection({
                name: 'Location',
                key: SubsectionKeys.location,
                types: [
                  MyLeadsFilterTypes.variables,
                  MyLeadsFilterTypes.excludedVariables,
                ],
                renderAsList: false,
                skipHide: true,
                children: (
                  <>
                    <MyLeadsLocationInputFilter
                      disabled={shouldDisableFilters}
                      filterParams={filterParams}
                      setFilterParams={setFilterParams}
                    />
                    <div style={{ marginTop: '8px' }}>
                      <div style={{ fontWeight: 500 }}>Excluding Locations</div>
                      <MyLeadsLocationInputFilter
                        disabled={shouldDisableFilters}
                        filterParams={filterParams}
                        setFilterParams={setFilterParams}
                        exclude
                      />
                    </div>
                  </>
                ),
              })}

              {renderSubsection({
                name: MyLeadsFriendlyName.LD_HOMEOWNRNT,
                key: SubsectionKeys.homeOwn,
                types: [MyLeadsFilterTypes.variables],
                renderAsList: true,
              })}
            </>
          ),
        })}

        {renderSection({
          name: 'Lifestyle',
          keys: SubsectionKeys.lifestyle,
          isAttribute: false,
          children: (
            <>
              {renderCheckbox('ldColAntiq', MyLeadsFriendlyName.LD_COL_ANTIQ)}
              {renderCheckbox('ldColArts', MyLeadsFriendlyName.LD_COL_ARTS)}
              {renderCheckbox('ldBeauty', MyLeadsFriendlyName.LD_BEAUTY)}
              {renderCheckbox('ldBksmagBks', MyLeadsFriendlyName.LD_BKSMAG_BKS)}
              {renderCheckbox('ldCatowner', MyLeadsFriendlyName.LD_CATOWNER)}
              {renderCheckbox('ldDonation', MyLeadsFriendlyName.LD_DONATION)}
              {renderCheckbox('ldChGen', MyLeadsFriendlyName.LD_CH_GEN)}
              {renderCheckbox('ldColCoin', MyLeadsFriendlyName.LD_COL_COIN)}
              {renderCheckbox('ldDogowner', MyLeadsFriendlyName.LD_DOGOWNER)}
              {renderCheckbox('ldColSport', MyLeadsFriendlyName.LD_COL_SPORT)}
            </>
          ),
        })}

        {renderSection({
          name: 'Individual',
          keys: [
            SubsectionKeys.age,
            SubsectionKeys.education,
            SubsectionKeys.gender,
            SubsectionKeys.lgbtSupport,
            SubsectionKeys.mrtlStatus,
            SubsectionKeys.numChild,
            SubsectionKeys.netWorth,
            SubsectionKeys.partyCode,
            SubsectionKeys.religion,
            ...SubsectionKeys.personal,
          ],
          isAttribute: false,
          children: (
            <>
              {renderSubsection({
                name: MyLeadsFriendlyName.age,
                key: SubsectionKeys.age,
                types: [MyLeadsFilterTypes.attributes],
                skipHide: true,
                renderAsList: false,
                children: (
                  <AgeSlider
                    minAge={minAge}
                    maxAge={maxAge}
                    disabled={shouldDisableFilters}
                    onAfterChange={onAfterAgeChange}
                    defaultValue={defaultAgeValue}
                    resetToDeafults={
                      !filterParams.query?.attributes?.[SubsectionKeys.age]
                    }
                  />
                ),
              })}

              {renderSubsection({
                name: MyLeadsFriendlyName.LD_EDUCATION,
                key: SubsectionKeys.education,
                types: [MyLeadsFilterTypes.variables],
                renderAsList: true,
              })}

              {renderSubsection({
                name: MyLeadsFriendlyName.gender,
                key: SubsectionKeys.gender,
                types: [MyLeadsFilterTypes.attributes],
                skipHide: true,
                renderAsList: true,
              })}

              {renderSubsection({
                name: MyLeadsFriendlyName.LD_PRFL_LGBT_SUPPORT,
                key: SubsectionKeys.lgbtSupport,
                types: [MyLeadsFilterTypes.variables],
                renderAsList: true,
              })}

              {renderSubsection({
                name: MyLeadsFriendlyName.LD_MRTLSTATUS,
                key: SubsectionKeys.mrtlStatus,
                types: [MyLeadsFilterTypes.variables],
                renderAsList: true,
              })}

              {renderSubsection({
                name: MyLeadsFriendlyName.LD_NUMCHILD,
                key: SubsectionKeys.numChild,
                types: [MyLeadsFilterTypes.variables],
                renderAsList: true,
              })}

              {renderSubsection({
                name: MyLeadsFriendlyName.LD_NETWORTH,
                key: SubsectionKeys.netWorth,
                types: [MyLeadsFilterTypes.variables],
                renderAsList: true,
              })}

              {renderSubsection({
                name: MyLeadsFriendlyName.LD_PARTY_CODE,
                key: SubsectionKeys.partyCode,
                types: [MyLeadsFilterTypes.variables],
                renderAsList: true,
              })}

              {renderSubsection({
                name: MyLeadsFriendlyName.LD_RELIGION,
                key: SubsectionKeys.religion,
                types: [MyLeadsFilterTypes.variables],
                renderAsList: true,
              })}

              {renderSubsection({
                name: 'Personal',
                key: SubsectionKeys.personal,
                types: [MyLeadsFilterTypes.variables],
                renderAsList: false,
                children: (
                  <>
                    {renderCheckbox(
                      'ldDieting',
                      MyLeadsFriendlyName.LD_DIETING,
                    )}
                    {renderCheckbox(
                      'ldPrflGunControl',
                      MyLeadsFriendlyName.LD_PRFL_GUN_CONTROL,
                    )}
                    {renderCheckbox(
                      'ldPrflMarijuanaReform',
                      MyLeadsFriendlyName.LD_PRFL_MARIJUANA_REFORM,
                    )}
                    {renderCheckbox(
                      'ldPrflMilSupport',
                      MyLeadsFriendlyName.LD_PRFL_MIL_SUPPORT,
                    )}
                    {renderCheckbox(
                      'ldSingparent',
                      MyLeadsFriendlyName.LD_SINGPARENT,
                    )}
                  </>
                ),
              })}
            </>
          ),
        })}

        {renderSection({
          name: 'Behaviour',
          keys: [...SubsectionKeys.interests, ...SubsectionKeys.sports],
          isAttribute: false,
          children: (
            <>
              {renderSubsection({
                name: 'Interests',
                key: SubsectionKeys.interests,
                types: [MyLeadsFilterTypes.variables],
                renderAsList: false,
                children: (
                  <>
                    {renderCheckbox(
                      'ldDonEnvir',
                      MyLeadsFriendlyName.LD_DON_ENVIR,
                    )}
                    {renderCheckbox(
                      'ldFoodWine',
                      MyLeadsFriendlyName.LD_FOOD_WINE,
                    )}
                    {renderCheckbox(
                      'ldFoodsnat',
                      MyLeadsFriendlyName.LD_FOODSNAT,
                    )}
                    {renderCheckbox(
                      'ldGamBoard',
                      MyLeadsFriendlyName.LD_GAM_BOARD,
                    )}
                    {renderCheckbox(
                      'ldGamCasino',
                      MyLeadsFriendlyName.LD_GAM_CASINO,
                    )}
                    {renderCheckbox(
                      'ldGamComp',
                      MyLeadsFriendlyName.LD_GAM_COMP,
                    )}
                    {renderCheckbox('ldGamVid', MyLeadsFriendlyName.LD_GAM_VID)}
                    {renderCheckbox('ldGaming', MyLeadsFriendlyName.LD_GAMING)}
                    {renderCheckbox(
                      'ldMotorcycle',
                      MyLeadsFriendlyName.LD_MOTORCYCLE,
                    )}
                    {renderCheckbox(
                      'ldScispace',
                      MyLeadsFriendlyName.LD_SCISPACE,
                    )}
                    {renderCheckbox(
                      'ldScubadiver',
                      MyLeadsFriendlyName.LD_SCUBADIVER,
                    )}
                    {renderCheckbox(
                      'ldSmoking',
                      MyLeadsFriendlyName.LD_SMOKING,
                    )}
                  </>
                ),
              })}

              {renderSubsection({
                name: 'Sports',
                key: SubsectionKeys.sports,
                types: [MyLeadsFilterTypes.variables],
                renderAsList: false,
                children: (
                  <>
                    {renderCheckbox(
                      'ldSpecBase',
                      MyLeadsFriendlyName.LD_SPEC_BASE,
                    )}
                    {renderCheckbox(
                      'ldBoating',
                      MyLeadsFriendlyName.LD_BOATING,
                    )}
                    {renderCheckbox(
                      'ldSpecHock',
                      MyLeadsFriendlyName.LD_SPEC_HOCK,
                    )}
                    {renderCheckbox('ldGolf', MyLeadsFriendlyName.LD_GOLF)}
                    {renderCheckbox('ldNascar', MyLeadsFriendlyName.LD_NASCAR)}
                    {renderCheckbox(
                      'ldSnowski',
                      MyLeadsFriendlyName.LD_SNOWSKI,
                    )}
                    {renderCheckbox(
                      'ldSpecSocc',
                      MyLeadsFriendlyName.LD_SPEC_SOCC,
                    )}
                    {renderCheckbox('ldTennis', MyLeadsFriendlyName.LD_TENNIS)}
                  </>
                ),
              })}
            </>
          ),
        })}
      </div>
    </div>
  );
};
